// Which brand a payment page wears.
//
// Three signals can claim the answer, so the order they are read in lives here,
// away from the form: the origin the link carries, the invoice number, and the
// hostname. Keeping it in one function is what lets the page and the links the
// API mints agree — the API stamps `origin` from the same CD rule (see
// OriginForInvoice in utils/paymentlink).

export type Brand = "pmh" | "cd";

// brandFromOrigin reads the parameter the portal puts on every link it mints,
// so a Collective invoice opens the Collective page even from the main host.
export function brandFromOrigin(origin?: string | null): Brand | null {
  const requested = (origin || "").trim().toLowerCase();
  if (requested === "cd") return "cd";
  if (requested === "pmh") return "pmh";
  return null;
}

// brandFromInvoiceNumber reads the number itself: Collective Design numbers its
// invoices with a CD prefix, and everything else is Pinnacle. This is what
// brands a link that carries no origin — the ones sent before the parameter
// existed — and a number a payer types by hand.
//
// The prefix is the whole rule, which is worth knowing: a CD invoice numbered
// without it would open as Pinnacle, and a Pinnacle invoice beginning with CD
// would open as Collective.
export function brandFromInvoiceNumber(
  invoiceNumber?: string | null,
): Brand | null {
  const number = (invoiceNumber || "").trim();
  if (!number) return null;
  return number.toUpperCase().startsWith("CD") ? "cd" : "pmh";
}

// brandFromHostname is the last resort: the main host is Pinnacle, and anything
// else is not (the Collective domain, a preview deployment, an old link). On a
// development host the name cannot say which brand is meant, so `?brand=`
// decides and defaults to Pinnacle.
export function brandFromHostname(
  hostname: string,
  brandParam?: string | null,
): Brand {
  const host = (hostname || "").trim().toLowerCase();
  const isDevelopmentHost =
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "::1" ||
    host.endsWith(".local");

  if (isDevelopmentHost) {
    return (brandParam || "").trim().toLowerCase() === "cd" ? "cd" : "pmh";
  }

  return host === "pp.thepinnaclecompanies.com" ||
    host === "pp-staging.thepinnaclecompanies.com"
    ? "pmh"
    : "cd";
}

// paymentBrand resolves the three signals in order of authority.
export function paymentBrand(input: {
  origin?: string | null;
  invoiceNumber?: string | null;
  hostname: string;
  brandParam?: string | null;
}): Brand {
  return (
    brandFromOrigin(input.origin) ??
    brandFromInvoiceNumber(input.invoiceNumber) ??
    brandFromHostname(input.hostname, input.brandParam)
  );
}
